/**
 * 노출 예약 윈도우 판정 — 홈 배너와 홈 팝업이 함께 쓴다.
 *
 * ★BE 규칙을 그대로 옮긴 것이다: 경계는 **start ≤ now < end**(시작 inclusive, 종료 exclusive)
 * 이며 null 은 해당 방향의 제약 없음을 뜻한다(둘 다 null = 상시 노출).
 * catalog-service 의 `HomeBanner.isWithinSchedule(Instant)` 와 홈 팝업의 노출 판정이 같은
 * 규칙이라, **구현을 한 곳에만 둔다.** 화면마다 따로 쓰면 규칙이 갈라진다 — 실제로 배너
 * 미리보기가 `isActive` 만 보고 기간 종료분까지 세어, 활성 69건 중 실제 노출은 12건인데 69로
 * 표시되던 문제가 있었다.
 *
 * 스토어프론트는 `isActive` 와 이 윈도우를 **함께** 보고 노출을 결정한다.
 */

export interface DisplayScheduleWindow {
  displayStartAt?: string | null;
  displayEndAt?: string | null;
}

/** 예약 윈도우 기준 현재 상태. 목록에서 "왜 노출되지 않는지"를 배지로 설명하는 데 쓴다. */
export type DisplayScheduleState =
  /** 지금 노출 구간 안(또는 상시 노출). */
  | 'live'
  /** 시작 전 — 아직 노출되지 않음. */
  | 'scheduled'
  /** 종료됨 — 더 이상 노출되지 않음. */
  | 'ended';

/** 파싱 실패/빈 값은 "제약 없음"으로 본다 — 알 수 없는 값 때문에 항목을 숨기지 않는다. */
const toTime = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : t;
};

export const displayScheduleState = (
  item: DisplayScheduleWindow,
  now: number = Date.now(),
): DisplayScheduleState => {
  const start = toTime(item.displayStartAt);
  const end = toTime(item.displayEndAt);
  if (start != null && now < start) return 'scheduled';
  // 종료는 exclusive — now 가 end 와 같으면 이미 끝난 것으로 본다(BE 와 동일).
  if (end != null && now >= end) return 'ended';
  return 'live';
};

/** 예약 윈도우 안인지. `isActive` 와 AND 로 묶어야 스토어프론트 노출 조건과 같아진다. */
export const isWithinDisplaySchedule = (
  item: DisplayScheduleWindow,
  now: number = Date.now(),
): boolean => displayScheduleState(item, now) === 'live';

/** 스토어프론트가 실제로 노출하는 항목인지 — 활성 + 예약 윈도우 안. */
export const isDisplayLive = (
  item: DisplayScheduleWindow & { isActive: boolean },
  now: number = Date.now(),
): boolean => item.isActive && isWithinDisplaySchedule(item, now);
